import { z } from "zod";

import { COMMAND_ERROR_CODES, PUBLIC_ERROR_CODES } from "./errors";

const strictObject = <Shape extends z.ZodRawShape>(shape: Shape) => z.object(shape).strict();

export const ParticipantIdSchema = z.string().uuid();
export const RoomIdSchema = z.string().uuid();
export const CardIdSchema = z.string().trim().min(1).max(128);
export const GoalIdSchema = z.string().trim().min(1).max(128);
export const VersionSchema = z.number().int().nonnegative();
export const JoinCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z2-9]{6,8}$/);
export const DisplayCodeSchema = z.string().trim().min(12).max(16);
export const DisplayNameSchema = z.string().trim().min(1).max(32);

export const RoomPhaseSchema = z.enum([
  "LOBBY",
  "PLAYING",
  "GOLD_SELECTION",
  "BETWEEN_ROUNDS",
  "COMPLETED",
  "ABANDONED",
]);
export const RoleSchema = z.enum(["MINER", "SABOTEUR"]);
export const ToolSchema = z.enum(["PICKAXE", "LANTERN", "CART"]);
export const RotationSchema = z.union([z.literal(0), z.literal(180)]);
export const GoalTruthSchema = z.enum(["GOLD", "ROCK"]);
export const PrivateCardKindSchema = z.enum([
  "PATH",
  "BREAK_TOOL",
  "REPAIR_TOOL",
  "MAP",
  "ROCKFALL",
]);

export const BoardPositionSchema = strictObject({
  x: z.number().int(),
  y: z.number().int(),
});

export const PublicPathCardSchema = strictObject({
  cardId: CardIdSchema,
  faceId: z.string().trim().min(1).max(128),
  x: z.number().int(),
  y: z.number().int(),
  rotation: RotationSchema,
});

export const PublicGoalSchema = strictObject({
  goalId: GoalIdSchema,
  x: z.number().int(),
  y: z.number().int(),
  revealed: z.boolean(),
  face: GoalTruthSchema.nullable().optional(),
  rotation: RotationSchema.optional(),
});

export const PublicBoardSchema = strictObject({
  paths: z.array(PublicPathCardSchema),
  goals: z.array(PublicGoalSchema).length(3),
});

export const AvatarIndexSchema = z.number().int().min(0).max(9);

export const PublicParticipantSchema = strictObject({
  participantId: ParticipantIdSchema,
  seatIndex: z.number().int().min(0).max(9),
  displayName: DisplayNameSchema,
  avatarIndex: AvatarIndexSchema.optional(),
  isHost: z.boolean(),
  ready: z.boolean(),
  connected: z.boolean(),
  brokenTools: z.array(ToolSchema).max(3),
  revealedRole: RoleSchema.nullable().optional(),
  finalGoldTotal: z.number().int().nonnegative().nullable().optional(),
  goldTotal: z.number().int().nonnegative().optional(),
});

export const PublicActionTypeSchema = z.enum([
  "PATH_PLACED",
  "TOOL_BROKEN",
  "TOOL_REPAIRED",
  "GOAL_INSPECTED",
  "ROCKFALL",
  "CARD_DISCARDED",
  "GOLD_CHOSEN",
  "LIFECYCLE",
]);

export const PublicActionSchema = strictObject({
  version: z.number().int().positive(),
  actorParticipantId: ParticipantIdSchema.nullable().optional(),
  type: PublicActionTypeSchema,
  summary: z.string().trim().min(1).max(256),
  targetParticipantId: ParticipantIdSchema.nullable().optional(),
  boardPosition: BoardPositionSchema.nullable().optional(),
});

export const RoundOutcomeSchema = strictObject({
  roundNumber: z.number().int().min(1).max(3),
  winningTeam: z.enum(["MINERS", "SABOTEURS", "NONE"]),
  unusedRole: RoleSchema,
});

export const FinalResultSchema = strictObject({
  participantId: ParticipantIdSchema,
  goldTotal: z.number().int().nonnegative(),
  winner: z.boolean(),
});

export const DisplayProjectionSchema = strictObject({
  roomId: RoomIdSchema,
  phase: RoomPhaseSchema,
  version: VersionSchema,
  joinCode: JoinCodeSchema.optional(),
  joinUrl: z.string().url().optional(),
  roundNumber: z.number().int().min(0).max(3),
  participants: z.array(PublicParticipantSchema).max(10),
  activeParticipantId: ParticipantIdSchema.nullable(),
  turnOrder: z.array(ParticipantIdSchema).max(10).optional(),
  drawPileCount: z.number().int().nonnegative(),
  discardCount: z.number().int().nonnegative(),
  board: PublicBoardSchema,
  recentActions: z.array(PublicActionSchema).max(50),
  roundOutcome: RoundOutcomeSchema.nullable().optional(),
  goldSharing: strictObject({
    totalGold: z.number().int().nonnegative(),
    totalCards: z.number().int().nonnegative(),
    remainingGold: z.number().int().nonnegative(),
    remainingCards: z.number().int().nonnegative(),
    choosingParticipantId: ParticipantIdSchema.nullable(),
    allocations: z
      .array(
        strictObject({
          participantId: ParticipantIdSchema,
          gold: z.number().int().nonnegative(),
          cardCount: z.number().int().nonnegative(),
        }),
      )
      .max(10),
  }).optional(),
  finalResults: z.array(FinalResultSchema).max(10).nullable().optional(),
});

export const PrivateCardSchema = strictObject({
  cardId: CardIdSchema,
  kind: PrivateCardKindSchema,
  faceId: z.string().trim().min(1).max(128),
});

export const InspectedGoalSchema = strictObject({
  goalId: GoalIdSchema,
  truth: GoalTruthSchema,
});

export const GoldCardSchema = strictObject({
  goldCardId: CardIdSchema,
  value: z.number().int().min(1).max(3),
});

export const LegalPathPlacementSchema = strictObject({
  cardId: CardIdSchema,
  position: BoardPositionSchema,
  rotations: z.array(RotationSchema).min(1).max(2),
});

export const LegalTargetsSchema = strictObject({
  cardId: CardIdSchema,
  participantIds: z.array(ParticipantIdSchema),
  goalIds: z.array(GoalIdSchema),
  boardPositions: z.array(BoardPositionSchema),
  tools: z.array(ToolSchema),
});

export const LegalCommandsSchema = strictObject({
  playableCardIds: z.array(CardIdSchema),
  pathPlacements: z.array(LegalPathPlacementSchema),
  targetsByCard: z.array(LegalTargetsSchema),
  discardableCardIds: z.array(CardIdSchema),
  canStartMatch: z.boolean(),
  canContinueRound: z.boolean(),
  canEndSession: z.boolean(),
});

export const HostControlsSchema = strictObject({
  canStartMatch: z.boolean(),
  canContinueRound: z.boolean(),
  canEndSession: z.boolean(),
});

export const PlayerSelfSchema = strictObject({
  roleAssignmentId: z.string().optional(),
  displayCode: DisplayCodeSchema.optional(),
  participantId: ParticipantIdSchema,
  role: RoleSchema.nullable(),
  hand: z.array(PrivateCardSchema),
  inspectedGoals: z.array(InspectedGoalSchema),
  goldAwards: z.array(GoldCardSchema),
  pendingGoldChoices: z.array(GoldCardSchema),
  legalCommands: LegalCommandsSchema,
  isCurrentTurn: z.boolean().optional(),
  hostControls: HostControlsSchema.optional(),
});

export const PlayerProjectionSchema = strictObject({
  public: DisplayProjectionSchema,
  self: PlayerSelfSchema,
});

export const CreateRoomRequestSchema = strictObject({
  displayName: DisplayNameSchema,
  avatarIndex: AvatarIndexSchema.optional(),
});
export const JoinRoomRequestSchema = strictObject({
  displayName: DisplayNameSchema,
  avatarIndex: AvatarIndexSchema.optional(),
});

export const PlayerBootstrapSchema = strictObject({
  roomId: RoomIdSchema,
  joinCode: JoinCodeSchema,
  displayCode: DisplayCodeSchema,
  participantId: ParticipantIdSchema,
  participantToken: z.string().min(1),
  recoveryUrl: z.string().min(1),
  version: VersionSchema,
  state: PlayerProjectionSchema,
});

export const PlayerStateResponseSchema = strictObject({
  version: VersionSchema,
  state: PlayerProjectionSchema,
});

export const DisplayStateResponseSchema = strictObject({
  version: VersionSchema,
  state: DisplayProjectionSchema,
});

export const PublicErrorCodeSchema = z.enum(PUBLIC_ERROR_CODES);
export const ErrorResponseSchema = strictObject({
  code: PublicErrorCodeSchema,
  message: z.string().min(1),
  requestId: z.string().min(1).optional(),
});

export const HealthResponseSchema = strictObject({ status: z.literal("ok") });

export const SocketAuthSchema = z.discriminatedUnion("audience", [
  strictObject({
    audience: z.literal("player"),
    roomCode: JoinCodeSchema,
    participantToken: z.string().min(1),
  }),
  strictObject({
    audience: z.literal("display"),
    displayCode: DisplayCodeSchema,
  }),
]);

const LobbySetReadyCommandSchema = strictObject({
  type: z.literal("lobby.set-ready"),
  ready: z.boolean(),
});
const MatchStartCommandSchema = strictObject({ type: z.literal("match.start") });
const PlayPathCommandSchema = strictObject({
  type: z.literal("turn.play-path"),
  cardId: CardIdSchema,
  x: z.number().int(),
  y: z.number().int(),
  rotation: RotationSchema,
});
const TargetToolCommandFields = {
  cardId: CardIdSchema,
  targetParticipantId: ParticipantIdSchema,
  tool: ToolSchema,
};
const BreakToolCommandSchema = strictObject({
  type: z.literal("turn.break-tool"),
  ...TargetToolCommandFields,
});
const RepairToolCommandSchema = strictObject({
  type: z.literal("turn.repair-tool"),
  ...TargetToolCommandFields,
});
const InspectGoalCommandSchema = strictObject({
  type: z.literal("turn.inspect-goal"),
  cardId: CardIdSchema,
  goalId: GoalIdSchema,
});
const RockfallCommandSchema = strictObject({
  type: z.literal("turn.rockfall"),
  cardId: CardIdSchema,
  x: z.number().int(),
  y: z.number().int(),
});
const DiscardCommandSchema = strictObject({
  type: z.literal("turn.discard"),
  cardId: CardIdSchema,
});
const ChooseGoldCommandSchema = strictObject({
  type: z.literal("reward.choose-gold"),
  goldCardId: CardIdSchema,
});
const ContinueRoundCommandSchema = strictObject({ type: z.literal("round.continue") });
const EndSessionCommandSchema = strictObject({ type: z.literal("session.end") });

export const GameCommandSchema = z.discriminatedUnion("type", [
  LobbySetReadyCommandSchema,
  MatchStartCommandSchema,
  PlayPathCommandSchema,
  BreakToolCommandSchema,
  RepairToolCommandSchema,
  InspectGoalCommandSchema,
  RockfallCommandSchema,
  DiscardCommandSchema,
  ChooseGoldCommandSchema,
  ContinueRoundCommandSchema,
  EndSessionCommandSchema,
]);

export const GameCommandEnvelopeSchema = strictObject({
  commandId: z.string().uuid(),
  expectedVersion: VersionSchema,
  command: GameCommandSchema,
});

export const CommandErrorCodeSchema = z.enum(COMMAND_ERROR_CODES);
export const CommandAckSchema = z.discriminatedUnion("status", [
  strictObject({ status: z.enum(["applied", "duplicate"]), version: VersionSchema }),
  strictObject({
    status: z.enum(["rejected", "conflict"]),
    code: CommandErrorCodeSchema,
    message: z.string().min(1),
    currentVersion: VersionSchema.optional(),
  }),
]);

const DisplayStateSnapshotSchema = strictObject({
  audience: z.literal("display"),
  version: VersionSchema,
  state: DisplayProjectionSchema,
});
const PlayerStateSnapshotSchema = strictObject({
  audience: z.literal("player"),
  version: VersionSchema,
  state: PlayerProjectionSchema,
});
export const StateSnapshotSchema = z.discriminatedUnion("audience", [
  DisplayStateSnapshotSchema,
  PlayerStateSnapshotSchema,
]);

export const SyncAckSchema = z.discriminatedUnion("status", [
  strictObject({ status: z.literal("ok"), snapshot: StateSnapshotSchema }),
  strictObject({
    status: z.literal("rejected"),
    code: z.enum(["UNAUTHORIZED", "ROOM_NOT_FOUND", "ROOM_EXPIRED"]),
  }),
]);

export const PresenceUpdateSchema = strictObject({
  version: VersionSchema,
  participants: z.array(
    strictObject({ participantId: ParticipantIdSchema, connected: z.boolean() }),
  ),
});

export const RoomExpiredSchema = strictObject({
  code: z.literal("ROOM_EXPIRED"),
  message: z.string().min(1),
});
